import React, { useState, useEffect, RefObject } from "react";
import { FlexRow } from "@/ui/FlexContainer/FlexContainer";
import { MenuIcon } from "@/assets/icons";
import { MascotLogo } from "@/features/HomepageMascot/MascotLogo";
import wordmark from "@/assets/images/logo.png";
import { Link, useLocation } from "react-router-dom";
import LightBalance from "@/components/LightBalance/LightBalance";
import cls from "./styles.module.scss";
import { Menu } from "../Menu/Menu";
import { useNotificationsDataContext } from "@/contexts/NotificationsContext";
import { useTranslation } from "react-i18next";

interface HeaderProps {
  wrapperRef?: RefObject<HTMLDivElement | null>;
}

export default function Header({ wrapperRef }: HeaderProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { unreadCount, reloadNotifications } = useNotificationsDataContext();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const isMainOrPetPage =
    location.pathname === "/" || /^\/pets\/[a-f0-9-]+$/.test(location.pathname);

  const [hasBg, setHasBg] = useState(false);

  useEffect(() => {
    const container = wrapperRef?.current;

    const handleScroll = () => {
      if (!container) return;

      const { scrollTop } = container;
      setHasBg(scrollTop > 1);
    };

    container?.addEventListener("scroll", handleScroll);

    return () => {
      container?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    if (!isMenuOpen) void reloadNotifications();
    setIsMenuOpen((current) => !current);
  };

  return (
    <div
      data-site-header
      data-menu-open={isMenuOpen}
      className={`${isMainOrPetPage ? cls.wrapperTransparent : cls.wrapper} ${
        hasBg ? cls.scrolled : ""
      }`}
    >
      <FlexRow justifyContent="space-between" className={cls.header}>
        <div className={cls.brand}>
          <Link to="/" className={cls.logoLink} aria-label={t("common.home")}>
            <span className={cls.mascotHome} aria-hidden="true">
              {location.pathname === "/" ? (
                <span data-mascot-home />
              ) : (
                <MascotLogo />
              )}
            </span>
            <img className={cls.wordmark} src={wordmark} alt="Светлячки" />
          </Link>
        </div>
        <FlexRow gap={24} className={cls.controls}>
          <LightBalance />
          <button
            type="button"
            className={cls.menuButton}
            onClick={toggleMenu}
            aria-label={t("common.openMenu")}
            aria-expanded={isMenuOpen}
          >
            <MenuIcon />
            {unreadCount > 0 && (
              <span className={cls.notificationBadge} aria-hidden="true">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        </FlexRow>

        <Menu isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      </FlexRow>
    </div>
  );
}
